<script lang="ts" setup>
import { computed } from 'vue'
import type { WelfarePageConfig } from '../configs/types'
import WelfareGuideAddScreen from './modules/WelfareGuideAddScreen.vue'
import WelfareGuideOpen from './modules/WelfareGuideOpen.vue'
import { TASK_TYPE } from '@/utils/enum'

/**
 * NovelMello 福利中心视图
 * props / emits 与其它品牌视图完全一致，不新增、不删减，逻辑全部由 index.vue 提供
 */
const props = defineProps<{
  config: WelfarePageConfig
  homeBgUrl: string
  btnBgColor: string
  signed: boolean
  progress: any[]
  modalSigned: boolean
  progressInfo: any
  tomorrowInfo: any
  checkInDayNum: number
  showTabbar: boolean
  tasks: any[]
  showGuidePopupAddHome: boolean
  showGuideOpen: boolean
  finishPopup: boolean
  coins: number
  completeTaskDescription: string
  userCoinBalance: number
  getBtnStatus: (item: any) => string
  onCheckIn: () => void
  handleTask: (item: any) => void
  onCheckInOk: () => void
}>()

const emit = defineEmits<{
  (e: 'update:modal-signed', value: boolean): void
  (e: 'update:show-guide-popup-add-home', value: boolean): void
  (e: 'update:show-guide-open', value: boolean): void
  (e: 'update:finish-popup', value: boolean): void
}>()

// v-model 桥接（与 novelarc 视图同款写法）
const modalSignedLocal = computed({
  get: () => props.modalSigned,
  set: (val: boolean) => emit('update:modal-signed', val),
})
const finishPopupLocal = computed({
  get: () => props.finishPopup,
  set: (val: boolean) => emit('update:finish-popup', val),
})

// ---- 主题（scoped style 里用 v-bind 消费；弹窗内因 portal 需用内联 style）----
const headerGradient = computed(() => props.config.theme.headerGradient || 'linear-gradient(120deg, #FDE0EF 0%, #EDE4FD 100%)')
const headerPillBg = computed(() => props.config.theme.headerPillBg || 'rgba(255, 255, 255, 0.7)')
const todayGradient = computed(() => props.config.theme.todayGradient || 'linear-gradient(180deg, #F0509B 0%, #9B5CF0 100%)')
const dayCellBg = computed(() => props.config.theme.dayCellBg || '#F8F1F7')
const pinkSoft = computed(() => props.config.theme.pinkSoft || '#FDEFF6')
const pinkSoftText = computed(() => props.config.theme.pinkSoftText || '#F0509B')
const cardShadow = computed(() => props.config.theme.cardShadow || '0 4px 16px rgba(124, 77, 232, 0.08)')
const dividerColor = computed(() => props.config.theme.dividerColor || '#F2EAF4')
const actionGradient = computed(() => props.config.theme.actionGradient || 'linear-gradient(90deg, #F0509B 0%, #9B5CF0 100%)')
const checkinBtnGradient = computed(() => props.config.theme.checkinBtnGradient || actionGradient.value)
const goldColor = computed(() => props.config.theme.goldColor || '#FFB23E')
const taskCoinsColor = computed(() => props.config.theme.taskCoinsColor || '#E8973F')
const giftColor = computed(() => props.config.theme.giftColor || props.config.theme.btnBg)

// ---- 文案兜底 ----
const rewardsTitle = computed(() => props.config.texts.rewardsTitle || 'Rewards')
const todayText = computed(() => props.config.texts.today || 'Today')

/**
 * 任务展示列表（纯展示过滤，不改 index.vue 的接口过滤与任何状态）
 * NovelMello 站点无登录功能，因此不渲染「绑定三方账号」这类会引向登录页的任务项
 */
const displayTasks = computed(() => (props.tasks || []).filter(item => item?.taskType !== TASK_TYPE.BIND))

/**
 * 「今日」所在下标（纯展示派生，不改任何业务状态）
 * - 未签到：checkInDayNum 就是今天要签的那一格
 * - 已签到：checkInDayNum - 1 即今天刚签完的那一格
 */
const todayIndex = computed(() => (props.signed ? props.checkInDayNum - 1 : props.checkInDayNum))

function isToday(index: number) {
  return index === todayIndex.value
}
/** 今日之前已完成的日期格 */
function isDone(item: any, index: number) {
  return !!item?.isSign && !isToday(index)
}
/** 末位为大奖格，用礼物图标 */
function isLastDay(index: number) {
  return index === props.progress.length - 1
}

/**
 * 任务按钮是否使用渐变底（设计中 Watch 类任务为渐变，Go / Ok 为 pink-soft）
 * 仅按 getBtnStatus 的既有返回值判断，不新增任何任务状态逻辑
 */
function isGradientBtn(item: any) {
  const label = props.getBtnStatus(item)
  return !!label && label !== props.config.texts.go && label !== props.config.texts.ok
}
</script>

<template>
  <div class="nm-rewards min-h-100dvh bg-n-bg-global pb-96px">
    <!-- 头部：浅渐变 + 标题 + 金币胶囊 -->
    <div class="nm-header px-16px pt-18px pb-14px flex justify-between items-center">
      <h1 class="nm-title text-24px text-n-text-1">
        {{ rewardsTitle }}
      </h1>
      <div class="nm-pill flex items-center px-12px py-6px">
        <i-lucide-coins class="w-16px h-16px" :style="{ color: goldColor }" />
        <span class="ml-6px text-13px font-800 text-n-text-1">{{ userCoinBalance }}</span>
        <span class="ml-4px text-13px font-800 text-n-text-1">{{ config.texts.coins }}</span>
      </div>
    </div>

    <!-- Daily Check-in 白卡 -->
    <div class="nm-card mx-16px mt-12px p-16px bg-n-bg-card">
      <h3 class="nm-title text-17px text-n-text-1">
        {{ config.texts.dailyCheckIn }}
      </h3>
      <p class="mt-2px text-12px text-n-text-3">
        {{ config.texts.youHaveCheckedIn }} {{ checkInDayNum }} {{ config.texts.dayStraight }}
      </p>

      <!-- 7 日格（横排，用 space-x 而非 gap，规避 flex-gap 兼容扫描） -->
      <div v-if="progress.length" class="mt-14px flex items-stretch space-x-6px">
        <div
          v-for="(item, index) in progress"
          :key="item.num"
          class="nm-day flex-1 min-w-0 flex flex-col items-center justify-center py-10px"
          :class="isToday(index) ? 'nm-day--today' : 'nm-day--normal'"
        >
          <!-- 今日 -->
          <template v-if="isToday(index)">
            <i-lucide-circle-check-big class="w-20px h-20px text-white" />
            <span class="mt-4px text-10px font-700 text-white">+{{ item.prizeNum }}</span>
            <span class="mt-2px text-9px nm-today-tag">{{ todayText }}</span>
          </template>
          <!-- 已完成 -->
          <template v-else-if="isDone(item, index)">
            <i-lucide-circle-check-big class="w-20px h-20px text-n-primary" />
            <span class="mt-4px text-11px font-700 text-n-text-2">+{{ item.prizeNum }}</span>
            <span class="mt-2px text-9px text-n-text-3">{{ config.texts.day(item.num) }}</span>
          </template>
          <!-- 未签到（末位为礼物大奖格） -->
          <template v-else>
            <i-lucide-gift v-if="isLastDay(index)" class="w-20px h-20px" :style="{ color: giftColor }" />
            <i-lucide-coins v-else class="w-20px h-20px" :style="{ color: goldColor }" />
            <span class="mt-4px text-11px font-700 text-n-text-2">+{{ item.prizeNum }}</span>
            <span class="mt-2px text-9px text-n-text-3">{{ config.texts.day(item.num) }}</span>
          </template>
        </div>
      </div>

      <!-- Check in 通栏渐变胶囊（文案三分支与原逻辑一致） -->
      <a
        v-if="progress.length"
        class="nm-checkin-btn block mt-14px w-full h-46px lh-46px text-center text-15px font-800 text-white"
        :class="{ 'opacity-70': signed }"
        @click="onCheckIn"
      >
        <span v-if="!signed">{{ config.texts.checkIn }}</span>
        <span v-else-if="checkInDayNum > 0 && checkInDayNum === progress.length">{{ config.texts.tomorrowToGetMore }}</span>
        <span v-else>{{ config.texts.tomorrowCheckIn }} {{ tomorrowInfo?.prizeNum }}</span>
      </a>
    </div>

    <!-- Tasks 白卡 -->
    <div v-if="displayTasks.length" class="nm-card mx-16px mt-12px px-16px py-16px bg-n-bg-card">
      <h3 class="nm-title text-17px text-n-text-1">
        {{ config.texts.tasks }}
      </h3>

      <div
        v-for="(item, index) in displayTasks"
        :key="index"
        class="nm-task-row flex justify-between items-center py-12px"
      >
        <div class="flex-1 min-w-0 pr-12px">
          <div class="text-14px font-700 text-n-text-1 truncate">
            {{ item.taskTitle }}
          </div>
          <div class="mt-4px flex items-center">
            <i-lucide-coins class="w-13px h-13px" :style="{ color: goldColor }" />
            <span class="ml-4px text-11px font-700" :style="{ color: taskCoinsColor }">+{{ item.coins }}</span>
          </div>
        </div>

        <div
          v-if="item.buttonState !== 1"
          class="nm-task-btn shrink-0 text-center px-16px py-7px text-12px font-800"
          :class="isGradientBtn(item) ? 'nm-task-btn--gradient' : 'nm-task-btn--soft'"
          @click="handleTask(item)"
        >
          {{ getBtnStatus(item) }}
        </div>
        <div v-else class="shrink-0 w-60px flex items-center justify-center">
          <i-lucide-circle-check-big class="w-18px h-18px text-n-primary" />
        </div>
      </div>
    </div>

    <!-- 引导弹窗（原样保留） -->
    <WelfareGuideAddScreen :value="showGuidePopupAddHome" @input="emit('update:show-guide-popup-add-home', $event)" />
    <WelfareGuideOpen :value="showGuideOpen" @input="emit('update:show-guide-open', $event)" />

    <!-- 签到成功弹窗：内部经 portal 渲染，样式一律走内联 style -->
    <BasicModal v-model="modalSignedLocal">
      <div class="p-20px">
        <div class="text-17px lh-2em font-700 text-center text-n-text-1">
          <p>{{ config.texts.checkInGet }} <span class="text-n-primary">{{ progressInfo?.prizeNum }}</span> {{ config.texts.coins }}</p>
        </div>
        <a
          class="block mt-16px w-full h-46px lh-46px text-15px font-800 text-white text-center"
          :style="{ background: actionGradient, borderRadius: '23px' }"
          @click="onCheckInOk"
        >
          {{ config.texts.ok }}
        </a>
      </div>
    </BasicModal>

    <!-- 任务领取成功弹窗 -->
    <BasicModal v-model="finishPopupLocal">
      <div class="p-20px">
        <div class="text-17px lh-2em font-700 text-center text-n-text-1">
          <p>{{ completeTaskDescription }} {{ config.texts.get }} <span class="text-n-primary">{{ coins }}</span> {{ config.texts.coins }}</p>
        </div>
        <a
          class="block mt-16px w-full h-46px lh-46px text-15px font-800 text-white text-center"
          :style="{ background: actionGradient, borderRadius: '23px' }"
          @click="finishPopupLocal = false"
        >
          {{ config.texts.ok }}
        </a>
      </div>
    </BasicModal>

    <!-- 底部导航：浮动胶囊样式方案见 02-home 计划，此处仅复用组件 -->
    <BasicTabbar v-if="showTabbar" tab="rewards" />
  </div>
</template>

<style scoped>
/* 标题字体：Baloo 2（--nm-font-display 声明在 htmls/novelmello.html） */
.nm-title {
  font-family: var(--nm-font-display, 'Baloo 2', 'Nunito Sans', -apple-system, BlinkMacSystemFont, sans-serif);
  font-weight: 700;
  line-height: 1.3;
}

/* 头部浅渐变 */
.nm-header {
  background: v-bind(headerGradient);
}

/* 头部金币胶囊 */
.nm-pill {
  background: v-bind(headerPillBg);
  border-radius: 15px;
}

/* 白卡 */
.nm-card {
  border-radius: 20px;
  box-shadow: v-bind(cardShadow);
}

/* 签到日格 */
.nm-day {
  border-radius: 12px;
}

.nm-day--normal {
  background: v-bind(dayCellBg);
}

.nm-day--today {
  background: v-bind(todayGradient);
}

.nm-today-tag {
  color: rgba(255, 255, 255, 0.8);
}

/* Check in 按钮 */
.nm-checkin-btn {
  background: v-bind(checkinBtnGradient);
  border-radius: 23px;
}

/* 任务行分割线 */
.nm-task-row {
  border-bottom: 1px solid v-bind(dividerColor);
}

.nm-task-row:last-child {
  border-bottom: 0;
}

/* 任务按钮 */
.nm-task-btn {
  border-radius: 999px;
  min-width: 60px;
}

.nm-task-btn--soft {
  background: v-bind(pinkSoft);
  color: v-bind(pinkSoftText);
}

.nm-task-btn--gradient {
  background: v-bind(actionGradient);
  color: #FFFFFF;
}
</style>
